import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import MermaidDiagram, { validateMermaid } from '../components/MermaidDiagram';

const BLANK = {
  title: '', blurb: '', category: 'General', mermaid: '',
  steps: [], your_role: '', context: [], sort_order: 100, active: true,
};

// ── Step documents ────────────────────────────────────────────────────────────
// A step can carry one document, stored in the private 'help-docs' bucket under
// <slug>/ (db_schema/help_docs.sql). The step itself holds doc_path + doc_name.
const DOC_BUCKET = 'help-docs';

const stepDocPaths = (steps) => (steps || []).map((s) => s.doc_path).filter(Boolean);

// Files only an abandoned draft points at, or that a saved draft dropped.
const removeDocs = (paths) => {
  if (paths.length) supabase.storage.from(DOC_BUCKET).remove(paths);
};

// PDFs open in a new tab; anything else (Word) downloads under its own name.
const DocLink = ({ path, name }) => {
  const [error, setError] = useState(null);
  const isPdf = /\.pdf$/i.test(name || path);

  const open = async () => {
    setError(null);
    const { data, error: err } = await supabase.storage
      .from(DOC_BUCKET)
      .createSignedUrl(path, 300, isPdf ? undefined : { download: name });
    if (err) { setError(`Could not open — ${err.message}`); return; }
    window.open(data.signedUrl, '_blank', 'noopener');
  };

  return (
    <div className="help-steps__doc">
      <button className="action-btn action-btn--ghost" onClick={open}>
        📄 {name} {isPdf ? '(opens PDF)' : '(download)'}
      </button>
      {error && <span className="help-edit__error">{error}</span>}
    </div>
  );
};

const StepDocEditor = ({ slug, item, patch }) => {
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    // Timestamp prefix so re-uploading a file of the same name never collides
    // with the copy the saved page still points at.
    const safe = file.name.replace(/[^\w.\- ]+/g, '_');
    const path = `${slug}/${Date.now()}-${safe}`;
    const { error: err } = await supabase.storage
      .from(DOC_BUCKET)
      .upload(path, file, { contentType: file.type || undefined });
    setBusy(false);
    if (err) { setError(`Upload failed — ${err.message}`); return; }
    patch({ doc_path: path, doc_name: file.name });
  };

  return (
    <div className="help-edit__doc">
      {item.doc_path ? (
        <>
          <span className="help-edit__hint">📄 {item.doc_name}</span>
          <button className="action-btn action-btn--ghost" onClick={() => patch({ doc_path: '', doc_name: '' })}>
            ✕ Remove document
          </button>
        </>
      ) : (
        <label className="action-btn action-btn--ghost">
          {busy ? 'Uploading…' : '📎 Attach document'}
          <input type="file" hidden disabled={busy} onChange={upload} />
        </label>
      )}
      {error && <span className="help-edit__error">{error}</span>}
    </div>
  );
};

// ── Repeaters ─────────────────────────────────────────────────────────────────
// Steps and context are jsonb arrays. Editing them as raw JSON would be a trap
// for a non-developer, so each gets add/remove/reorder rows instead.
const Repeater = ({ label, items, onChange, fields, addLabel, renderExtra }) => {
  const update = (i, key, value) =>
    onChange(items.map((it, n) => (n === i ? { ...it, [key]: value } : it)));
  const patch = (i) => (values) =>
    onChange(items.map((it, n) => (n === i ? { ...it, ...values } : it)));

  const remove = (i) => onChange(items.filter((_, n) => n !== i));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="help-edit__block">
      <label className="help-edit__label">{label}</label>
      {items.map((item, i) => (
        <div key={i} className="help-edit__row">
          <div className="help-edit__rownum">{i + 1}</div>
          <div className="help-edit__rowfields">
            {fields.map((f) => (
              f.multiline ? (
                <textarea
                  key={f.key}
                  className="help-edit__input"
                  rows={3}
                  placeholder={f.placeholder}
                  value={item[f.key] || ''}
                  onChange={(e) => update(i, f.key, e.target.value)}
                />
              ) : (
                <input
                  key={f.key}
                  className="help-edit__input"
                  placeholder={f.placeholder}
                  value={item[f.key] || ''}
                  onChange={(e) => update(i, f.key, e.target.value)}
                />
              )
            ))}
            {renderExtra && renderExtra(item, patch(i))}
          </div>
          <div className="help-edit__rowbtns">
            <button className="action-btn action-btn--ghost" onClick={() => move(i, -1)} disabled={i === 0}>↑ Up</button>
            <button className="action-btn action-btn--ghost" onClick={() => move(i, 1)} disabled={i === items.length - 1}>↓ Down</button>
            <button className="action-btn action-btn--ghost" onClick={() => remove(i)}>✕ Remove</button>
          </div>
        </div>
      ))}
      <button
        className="action-btn action-btn--ghost"
        onClick={() => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, '']))])}
      >
        + {addLabel}
      </button>
    </div>
  );
};

// ── Editor ────────────────────────────────────────────────────────────────────
// Deliberately not a live split-pane preview: the topic page renders the
// diagram on load, and editing replaces that view entirely. Save validates the
// diagram first, so a broken one is never stored.
const Editor = ({ slug, draft, setDraft, onSave, onCancel, saving, error }) => {
  const set = (key) => (e) => setDraft({ ...draft, [key]: e.target.value });

  return (
    <div className="help-edit">
      <div className="help-edit__block">
        <label className="help-edit__label">Title</label>
        <input className="help-edit__input" value={draft.title} onChange={set('title')} />
      </div>

      <div className="help-edit__block">
        <label className="help-edit__label">Summary — one line, shown on the help list</label>
        <input className="help-edit__input" value={draft.blurb || ''} onChange={set('blurb')} />
      </div>

      <div className="help-edit__grid2">
        <div className="help-edit__block">
          <label className="help-edit__label">Category</label>
          <input className="help-edit__input" value={draft.category || ''} onChange={set('category')} />
        </div>
        <div className="help-edit__block">
          <label className="help-edit__label">Sort order within category</label>
          <input
            className="help-edit__input"
            type="number"
            value={draft.sort_order}
            onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
          />
        </div>
      </div>

      <div className="help-edit__block">
        <label className="help-edit__label">Diagram — mermaid source</label>
        <textarea
          className="help-edit__input help-edit__mermaid"
          rows={18}
          spellCheck={false}
          value={draft.mermaid || ''}
          onChange={set('mermaid')}
        />
        <div className="help-edit__hint">
          Checked when you save — if the syntax is wrong it tells you why and nothing is stored.
        </div>
      </div>

      <Repeater
        label="Numbered steps — shown under the diagram"
        items={draft.steps || []}
        onChange={(steps) => setDraft({ ...draft, steps })}
        addLabel="Add step"
        fields={[
          { key: 'title',  placeholder: 'Step heading' },
          { key: 'detail', placeholder: 'What happens at this step', multiline: true },
        ]}
        renderExtra={(item, patch) => <StepDocEditor slug={slug} item={item} patch={patch} />}
      />

      <div className="help-edit__block">
        <label className="help-edit__label">Your role — what the staff member is responsible for</label>
        <textarea className="help-edit__input" rows={5} value={draft.your_role || ''} onChange={set('your_role')} />
      </div>

      <Repeater
        label="Extra context — optional sections after the steps"
        items={draft.context || []}
        onChange={(context) => setDraft({ ...draft, context })}
        addLabel="Add section"
        fields={[
          { key: 'heading', placeholder: 'Section heading' },
          { key: 'body',    placeholder: 'Section text', multiline: true },
        ]}
      />

      {error && <div className="help-edit__error">{error}</div>}

      <div className="help-edit__actions">
        <button className="action-btn" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="action-btn action-btn--ghost" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const HelpTopic = () => {
  const { slug }            = useParams();
  const navigate            = useNavigate();
  const [params, setParams] = useSearchParams();

  const [topic, setTopic]     = useState(null);
  const [draft, setDraft]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const editing = params.get('edit') === '1';

  const load = useCallback(async () => {
    setLoading(true);
    // .limit(1) not .single() -- an unknown slug is a normal "not found" page,
    // not an exception.
    const { data } = await supabase.from('help_topics').select('*').eq('slug', slug).limit(1);
    setTopic(data?.[0] || null);
    setLoading(false);
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Draft is seeded when edit mode opens so Cancel can just discard it.
  useEffect(() => {
    if (editing && topic && !draft) {
      setDraft({ ...BLANK, ...topic, steps: topic.steps || [], context: topic.context || [] });
    }
    if (!editing && draft) setDraft(null);
  }, [editing, topic, draft]);

  const startEdit = () => { setError(null); setParams({ edit: '1' }); };
  const cancel    = () => {
    // Uploads made in this draft were never saved onto the page -- drop them.
    const kept = new Set(stepDocPaths(topic.steps));
    removeDocs(stepDocPaths(draft?.steps).filter((p) => !kept.has(p)));
    setError(null); setDraft(null); setParams({});
  };

  const save = async () => {
    setSaving(true);
    setError(null);

    const diagramError = await validateMermaid(draft.mermaid);
    if (diagramError) {
      setSaving(false);
      setError(`Nothing saved — the diagram has a problem: ${diagramError}`);
      return;
    }

    const { error: err } = await supabase
      .from('help_topics')
      .update({
        title:      draft.title,
        blurb:      draft.blurb,
        category:   draft.category || 'General',
        mermaid:    draft.mermaid,
        steps:      draft.steps,
        your_role:  draft.your_role,
        context:    draft.context,
        sort_order: draft.sort_order,
        updated_at: new Date().toISOString(),
      })
      .eq('slug', slug);

    setSaving(false);
    if (err) { setError(`Could not save — ${err.message}`); return; }

    // Only once the save has landed: files the saved page no longer points at.
    const kept = new Set(stepDocPaths(draft.steps));
    removeDocs(stepDocPaths(topic.steps).filter((p) => !kept.has(p)));

    setDraft(null);
    setParams({});
    load();
  };

  if (loading) {
    return <main className="app-main"><div className="loading-row">Loading…</div></main>;
  }

  if (!topic) {
    return (
      <main className="app-main">
        <div className="page-nav">
          <button className="back-btn" onClick={() => navigate('/help')}>← Help</button>
          <span className="page-nav__title">Topic not found</span>
        </div>
        <section className="section">
          <div className="empty-row">No help topic with the address “{slug}”.</div>
        </section>
      </main>
    );
  }

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/help')}>← All Topics</button>
        <span className="page-nav__title">{topic.title}</span>
        {!editing && (
          <button className="action-btn action-btn--ghost" onClick={startEdit}>✎ Edit Page</button>
        )}
      </div>

      <section className="section help-topic">
        {editing && draft ? (
          <Editor
            slug={slug}
            draft={draft}
            setDraft={setDraft}
            onSave={save}
            onCancel={cancel}
            saving={saving}
            error={error}
          />
        ) : (
          <>
            <h1 className="help-topic__title">{topic.title}</h1>
            {topic.blurb && <p className="help-topic__blurb">{topic.blurb}</p>}

            <MermaidDiagram code={topic.mermaid} />

            {!!(topic.steps || []).length && (
              <div className="help-topic__section">
                <h2 className="help-topic__h2">Step by step</h2>
                <ol className="help-steps">
                  {topic.steps.map((s, i) => (
                    <li key={i} className="help-steps__item">
                      {s.title && <div className="help-steps__title">{s.title}</div>}
                      {s.detail && <div className="help-steps__detail">{s.detail}</div>}
                      {s.doc_path && <DocLink path={s.doc_path} name={s.doc_name} />}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {topic.your_role && (
              <div className="help-topic__section help-role">
                <h2 className="help-topic__h2">Your role</h2>
                {topic.your_role.split('\n\n').map((p, i) => (
                  <p key={i} className="help-role__text">{p}</p>
                ))}
              </div>
            )}

            {(topic.context || []).map((c, i) => (
              <div key={i} className="help-topic__section">
                {c.heading && <h2 className="help-topic__h2">{c.heading}</h2>}
                {(c.body || '').split('\n\n').map((p, n) => (
                  <p key={n} className="help-topic__para">{p}</p>
                ))}
              </div>
            ))}

            {topic.updated_at && (
              <div className="help-topic__updated">
                Last updated {new Date(topic.updated_at).toLocaleDateString('en-NZ', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default HelpTopic;
